import {
  GeneralDataSchema,
  GeneralResMock,
  Meta as GeneralMeta,
  RelationshipBoolean,
  RelationshipsData,
  RelationType
} from './general.type'
import { ApiStrength } from './product.type'

export type MedicationDataMock = GeneralDataSchema<MedicationAttributes, MedRelationships, StatusMeta>
export type PatientMedicationDataMock = GeneralDataSchema<
  PatientMedicationAttributes,
  PatientMedicationRelationships,
  StatusMeta
>
export type PatientMedicationsResponse = GeneralResMock<
  PatientMedicationDataMock[],
  PatientMedicationIncluded[],
  PatientMedicationMeta
>

// export interface MedicationDataMock<T> {
//   data: T
//   included: MedicationIncluded[]
//   links: Links2
//   meta: Meta4
//   jsonapi: Jsonapi
// }

enum frequencyType {
  hourly = 'hourly',
  daily = 'daily'
}

enum periods {
  morning = 'morning',
  afternoon = 'afternoon',
  night = 'night'
}

export enum statusType {
  onhold = 'onhold',
  completed = 'completed',
  discontinued = 'discontinued',
  ongoing = 'ongoing',
  pending = 'pending',
  verified = 'verified',
  review_requested = 'review_requested',
  not_available = 'not_available'
}

export interface FrequencyData {
  interval?: string
  periods?: string[]
  specific_times?: string[]
  type: 'hourly' | 'daily'
}
// export interface MedicationDataMock {
//   id: string
//   type: string
//   attributes: MedicationAttributes
//   relationships: Relationships
//   meta: StatusMeta
// }

export interface StatusMeta {
  status: keyof typeof statusType
  query_note?: string | null
  last_transition?: {
    metadata?: {
      note?: string
      product_id?: string
      staff_id?: number
      staff_name?: string
    }
  }
}

/** Who supplies the medicine: the hospital pharmacy (default) or the patient buying it outside. */
export type SupplySource = 'in_house' | 'outside'

export const DEFAULT_SUPPLY_SOURCE: SupplySource = 'in_house'

export interface MedicationAttributes {
  medication_type: string
  nf_drug_name: string
  api_doses?: ApiStrength[]
  quantity: string
  dosage: string
  dose_quantity: string
  dose_unit: string
  context: string
  form: string
  strength: string
  route: string
  once: boolean
  administer_at_hospital?: boolean
  supply_source?: SupplySource
  custom_frequency: {
    type: keyof typeof frequencyType
    periods: (keyof typeof periods)[]
    interval: string
    specific_times: string[]
  }
  created_at: string
  updated_at: string
  modified_at: string | null
  administered_at: string | null
  prescribed: boolean
  reconciliation: boolean
  start_at: string
  end_at: string
  comment: string
  ordered: boolean
  locked_at: string | null
}

export interface PatientMedicationAttributes {
  medication_type: string
  nf_drug_name: string
  api_doses?: ApiStrength[]
  quantity: string
  dose_quantity: string
  dose_unit: string
  context: string
  form: string
  strength: string
  route: string
  once: boolean
  custom_frequency: FrequencyData
  prescribed: boolean
  modified_at: string | null
  start_at: string
  end_at: string
  next_dosage_at: string | null
  comment: string
  ordered: boolean
  locked_at: string | null
  created_at: string
  updated_at: string
  dosage?: string
  administered_at?: string | null
  reconciliation?: boolean
  supply_source?: SupplySource
}

export interface MedRelationships {
  patient: RelationType
  staff: MedStaff
  encounter: RelationType
  medical_histories: RelationshipsData
  medication_frequency: RelationType
  product: RelationType
  allergies: RelationshipsData
  allergy: RelationType
  intake: RelationshipsData
}

export interface PatientMedicationRelationships extends Omit<MedRelationships, 'allergies' | 'intake'> {
  allergies?: RelationshipsData
  intake: RelationType
}

export interface MedStaff {
  data: {
    type: string
    id: string
    attributes?: {
      first_name: string
      last_name: string
    }
  }
}

export interface MedicationIncluded {
  id: string
  type: string
  attributes: Attributes2
  relationships: Relationships2
  links: Links
  meta: Meta3
}

export interface PatientMedicationIncluded {
  id: string
  type: string
  attributes: Partial<Attributes2> & Record<string, unknown>
  relationships: Record<string, unknown>
  links: {
    self?: string
    related?: string
  }
  meta: Record<string, unknown>
}

export interface PatientMedicationMeta extends GeneralMeta {
  encounter_id?: number
}

export interface Attributes2 {
  first_name: string
  middle_name?: string
  status?: string
  last_name: string
  date_of_birth: string
  gender: string
  ref?: string
  phone_number: string
  created_at: string
  unique_id?: string
  title?: string
  marital_status?: string
  name?: string
  generic_name: string
  brand_name: string
  code?: string
  employer?: Employer
  choices?: any
  guardian_contact?: GuardianContact
  misc?: any
  updated_at: string
  employment_date?: string
  employment_type?: string
  specialisation?: string
  license_number?: string
  license_expiry_date?: string
  salary?: string
  end_date?: string
  reaction?: string
  encounter_type?: string
  start_date?: string
}

export interface Employer {
  state: string
  country: string
  industry: string
  employment: string
  occupation: string
  postal_code: string
  employer_address: string
  employment_address_line: string
}

export interface GuardianContact {
  city: string
  name: string
  email: string
  state: string
  gender: string
  address: string
  country: string
  work_phone: string
  postal_code: string
  phone_number: string
  relationship: string
}

export interface Relationships2 {
  user?: RelationshipBoolean
  department?: RelationshipBoolean
  icd_codes?: RelationshipsData
}

export interface Links {
  self: string
}

export interface Meta3 {
  roles: string[]
  status?: string
}

export interface Links2 {
  first: string
  last: string
  prev: any
  next: any
  self: string
}

export interface Meta4 {
  pagination: Pagination
  stats: Stats
}

export interface Pagination {
  scaffold_url: string
  first_url: string
  prev_url: string
  page_url: string
  next_url: string
  last_url: string
  count: number
  page: number
  limit: number
  vars: Vars
  pages: number
  last: number
  in: number
  from: number
  to: number
  prev: any
  next: any
  series: string[]
}

export interface Stats {
  pending: number
  verified: number
  review_requested: number
  ongoing: number
  total: number
}

export interface Vars {
  count_args: string[]
  ends: boolean
  limit: number
  outset: number
  page: number
  page_param: string
  size: number
  headers: Headers
  metadata: string[]
  steps: boolean
  limit_param: string
  limit_max: number
  limit_extra: boolean
  overflow: string
  jsonapi: boolean
  count: number
}

export interface Headers {
  page: string
  limit: string
  count: string
  pages: string
}

export interface Jsonapi {
  version: string
}
